import { computed, ref } from '@vue/composition-api';
import { useStorage, StorageSerializers } from '@vueuse/core';

export const LICENSE_ERROR_STATES = ['EXPIRED', 'LONG_EXPIRED'];

// Module-level singleton state (mirrors the `user`/`token` pattern in useAuth),
// so useAuth can import setLicense/triggerLicenseModal/clearLicense directly
// without provide/inject wiring.
// Explicit object serializer: with a `null` default @vueuse falls back to the
// 'any' serializer which writes objects as `String(obj)` → "[object Object]".
const license = useStorage('PixelMonitor_License', null, undefined, {
  serializer: StorageSerializers.object
});
const showLicenseModalOnce = ref(false);

export const useLicense = function() {
  const licenseSeverity = computed(() => {
    const data = license.value;
    if (!data || typeof data !== 'object') {
      return null;
    }
    if (
      data.valid === false ||
      (data.expired && LICENSE_ERROR_STATES.includes(data.expired))
    ) {
      return 'error';
    }
    if (data.expired === 'EXPIRING_SOON') {
      return 'warning';
    }
    return 'success';
  });

  const hasLicenseAlert = computed(
    () =>
      licenseSeverity.value === 'warning' || licenseSeverity.value === 'error'
  );

  const hasBlockingLicenseAlert = computed(
    () => licenseSeverity.value === 'error'
  );

  const licenseInfo = computed(() => {
    const data = license.value;
    return data && typeof data === 'object' ? data.info ?? null : null;
  });

  const setLicense = data => {
    if (data) {
      license.value = {
        expired: data.expired,
        valid: data.valid,
        info: data.info
      };
    }
  };

  const clearLicense = () => {
    license.value = null;
    showLicenseModalOnce.value = false;
  };

  const triggerLicenseModal = () => {
    if (hasBlockingLicenseAlert.value) {
      showLicenseModalOnce.value = true;
    }
  };

  const clearLicenseModal = () => {
    showLicenseModalOnce.value = false;
  };

  return {
    license,
    licenseSeverity,
    licenseInfo,
    hasLicenseAlert,
    hasBlockingLicenseAlert,
    showLicenseModalOnce,
    setLicense,
    clearLicense,
    triggerLicenseModal,
    clearLicenseModal
  };
};
